import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import Search from '@/components/common/search/Search';
import Controls from '@/components/projects/sorting/Sorting';
import { useDebounce } from '@/hooks/useDebounce/useDebounce';

import classes from './ProjectsHeader.module.scss';

interface ProjectsHeaderProps {
	handleRouterParams: (
		searchQuery?: string | null,
		ordering?: string[] | null
	) => void;
	isSingleColumn: boolean;
	handlerPreview: () => void;
}

const SORT_TYPES = {
	NAME: {
		ASC: 'name',
		DESC: '-name',
		NONE: '',
	},
	DATE: {
		ASC: 'date_start',
		DESC: '-date_start',
		NONE: '',
	},
} as const;

const ProjectsHeader = ({
	handleRouterParams,
	isSingleColumn,
	handlerPreview,
}: ProjectsHeaderProps) => {
	const [searchParams] = useSearchParams();

	const [searchQuery, setSearchQuery] = useState('');
	const [sortState, setSortState] = useState({
		name: '',
		date: '',
		ordering: [] as string[],
	});

	const handlerSortByName = useCallback(() => {
		setSortState((prev) => {
			let newName = '';
			let newOrdering = [...prev.ordering];

			if (!prev.name) {
				newName = SORT_TYPES.NAME.DESC;
				newOrdering.push(SORT_TYPES.NAME.DESC);
			} else if (prev.name === SORT_TYPES.NAME.DESC) {
				newName = SORT_TYPES.NAME.ASC;
				const index = newOrdering.indexOf(SORT_TYPES.NAME.DESC);
				newOrdering.splice(index, 1, SORT_TYPES.NAME.ASC);
			} else if (prev.name === SORT_TYPES.NAME.ASC) {
				newName = SORT_TYPES.NAME.NONE;
				newOrdering = newOrdering.filter((i) => i !== SORT_TYPES.NAME.ASC);
			}

			handleRouterParams(searchQuery, newOrdering);

			return {
				...prev,
				name: newName,
				ordering: newOrdering,
			};
		});
	}, [handleRouterParams, searchQuery]);

	const handlerByDate = useCallback(() => {
		setSortState((prev) => {
			let newDate = '';
			let newOrdering = [...prev.ordering];

			if (!prev.date) {
				newDate = SORT_TYPES.DATE.ASC;
				newOrdering.push(SORT_TYPES.DATE.ASC);
			} else if (prev.date === SORT_TYPES.DATE.ASC) {
				newDate = SORT_TYPES.DATE.DESC;
				const index = newOrdering.indexOf(SORT_TYPES.DATE.ASC);
				newOrdering.splice(index, 1, SORT_TYPES.DATE.DESC);
			} else if (prev.date === SORT_TYPES.DATE.DESC) {
				newDate = SORT_TYPES.DATE.NONE;
				newOrdering = newOrdering.filter((i) => i !== SORT_TYPES.DATE.DESC);
			}

			handleRouterParams(searchQuery, newOrdering);

			return {
				...prev,
				date: newDate,
				ordering: newOrdering,
			};
		});
	}, [handleRouterParams, searchQuery]);

	const handleSearch = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value;
			setSearchQuery(value);
			handleRouterParams(value, sortState.ordering);
		},
		[handleRouterParams, sortState.ordering]
	);

	const debouncedSearch = useDebounce(handleSearch, 500);

	useEffect(() => {
		const searchParam = searchParams.get('search') || '';
		const orderingParam = searchParams.get('ordering');
		const orderingArray = orderingParam?.split(',') || [];

		setSearchQuery(searchParam);
		setSortState({
			name:
				orderingArray.find(
					(i) => i === SORT_TYPES.NAME.ASC || i === SORT_TYPES.NAME.DESC
				) || '',
			date:
				orderingArray.find(
					(i) => i === SORT_TYPES.DATE.ASC || i === SORT_TYPES.DATE.DESC
				) || '',
			ordering: orderingArray,
		});
	}, [searchParams]);

	return (
		<div className={classes.container}>
			<Search
				onInput={debouncedSearch}
				placeholder='Название проекта'
				clazz={classes.search}
				initialValue={searchQuery}
				autoFocus={!!searchQuery}
			/>
			<Controls
				changeSortByName={handlerSortByName}
				sortByName={sortState.name}
				changeSortByDate={handlerByDate}
				sortByDate={sortState.date}
				changePreview={handlerPreview}
				isSingleColumn={isSingleColumn}
			/>
		</div>
	);
};

export default ProjectsHeader;
